import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCreerRdv } from "@/components/CreerRdv";
import { matchShortcut } from "@/lib/shortcuts";
import { looksLikePerson } from "@/lib/patientParse";
import {
  Search, User, Stethoscope, Calendar, Package, Zap, ArrowUpRight, Clock, RotateCcw, MailX, CreditCard, UserPlus,
} from "lucide-react";

const DEBOUNCE = 150;
const RECENTS_KEY = "sx-recents";
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const loadRecents = () => { try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]"); } catch { return []; } };
const pushRecent = (item) => {
  try {
    const cur = loadRecents().filter((x) => x.route !== item.route);
    localStorage.setItem(RECENTS_KEY, JSON.stringify([{ ...item, kind: "recent" }, ...cur].slice(0, 5)));
  } catch { /* noop */ }
};

const GROUP_META = {
  patients: { label: "Patients", Icon: User },
  soins: { label: "Soins", Icon: Stethoscope },
  rdvs: { label: "Rendez-vous", Icon: Calendar },
  prestations: { label: "Prestations", Icon: Package },
};

export default function CommandPalette() {
  const nav = useNavigate();
  const { openCreerRdv } = useCreerRdv();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ patients: [], soins: [], rdvs: [], prestations: [] });
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState([]);
  const timer = useRef(null);
  const inputRef = useRef(null);

  const openPalette = useCallback(() => { setQ(""); setResults({ patients: [], soins: [], rdvs: [], prestations: [] }); setActive(0); setRecents(loadRecents()); setOpen(true); }, []);

  // Raccourci ⌘K / Ctrl+K global + ouverture via événement (barre d'en-tête)
  useEffect(() => {
    const onKey = (e) => {
      if (matchShortcut(e, "command_palette")) { e.preventDefault(); openPalette(); }
    };
    const onOpen = () => openPalette();
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-command-palette", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("open-command-palette", onOpen); };
  }, [openPalette]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 20); }, [open]);

  // Recherche débouncée
  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults({ patients: [], soins: [], rdvs: [], prestations: [] }); return; }
    timer.current = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => { setResults(r.data); setActive(0); }).catch(() => {});
    }, DEBOUNCE);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open]);

  const runAction = useCallback((route, fn) => {
    setOpen(false);
    if (fn) { fn(); return; }
    if (route) nav(route);
  }, [nav]);

  const ACTIONS = useMemo(() => [
    { titre: "Créer RDV", Icon: Calendar, fn: () => openCreerRdv() },
    { titre: "Nouveau patient", Icon: User, route: "/patients?newpatient=1" },
    { titre: "Nouvelle tournée", Icon: ArrowUpRight, route: "/tournees?new=1" },
    { titre: "Aller à l'Agenda", Icon: Calendar, route: "/agenda" },
    { titre: "Aller à Piloter", Icon: Zap, route: "/piloter" },
    { titre: "Paramètres › Catalogue…", Icon: Package, route: "/prestations" },
  ], [openCreerRdv]);

  const SUGGESTIONS = [
    { titre: "À relancer", Icon: RotateCcw, route: "/patients?filtre=a_relancer" },
    { titre: "Sans email", Icon: MailX, route: "/patients?sansEmail=1" },
    { titre: "Impayés", Icon: CreditCard, route: "/piloter/paiements?statut=non_solde" },
  ];

  // Construction de la liste ordonnée (pour la navigation clavier) + rendu par groupe
  const { groups, flat } = useMemo(() => {
    const g = [];
    const f = [];
    const add = (item) => { f.push(item); return f.length - 1; };
    if (!q.trim()) {
      if (recents.length) g.push({ key: "recents", label: "Récents", Icon: Clock, rows: recents.map((it) => ({ ...it, idx: add({ route: it.route }) })) });
      g.push({ key: "suggestions", label: "Suggestions", Icon: Zap, rows: SUGGESTIONS.map((it) => ({ ...it, idx: add({ route: it.route }) })) });
    } else {
      for (const key of ["patients", "soins", "rdvs", "prestations"]) {
        const rows = (results[key] || []).map((it) => ({ ...it, idx: add({ route: it.route, save: it }) }));
        if (rows.length) g.push({ key, label: GROUP_META[key].label, Icon: GROUP_META[key].Icon, rows });
      }
      const nq = norm(q);
      const actRows = ACTIONS.filter((a) => norm(a.titre).includes(nq)).map((a) => ({ ...a, idx: add({ route: a.route, fn: a.fn }) }));
      if (actRows.length) g.push({ key: "actions", label: "Actions", Icon: Zap, rows: actRows });
      // Ligne « Créer le patient « … » » (maquette 7c)
      if (looksLikePerson(q)) {
        const cq = q.trim();
        const route = `/patients?newpatient=1&npq=${encodeURIComponent(cq)}`;
        g.push({ key: "create", label: "", Icon: UserPlus, noResults: (results.patients || []).length === 0, rows: [{ titre: `Créer le patient « ${cq} »`, Icon: UserPlus, create: true, idx: add({ route }) }] });
      }
    }
    return { groups: g, flat: f };
  }, [q, results, recents, ACTIONS]); // eslint-disable-line

  const select = useCallback((i) => {
    const item = flat[i];
    if (!item) return;
    if (item.save) pushRecent({ titre: item.save.titre, contexte: item.save.contexte, route: item.save.route });
    runAction(item.route, item.fn);
  }, [flat, runAction]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); select(active); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center" style={{ background: "rgba(20,24,33,.25)", paddingTop: "12vh" }}
      onClick={() => setOpen(false)} data-testid="command-palette">
      <div className="bg-white overflow-hidden" style={{ width: 640, maxWidth: "92vw", borderRadius: 14, boxShadow: "var(--sh-float)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 px-4" style={{ height: 52, borderBottom: "1px solid var(--border)" }}>
          <Search className="w-[18px] h-[18px]" style={{ color: "var(--muted-2)" }} />
          <input ref={inputRef} data-testid="command-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Rechercher un patient, un soin, un RDV…" className="flex-1 bg-transparent outline-none" style={{ fontSize: 15, color: "var(--ink)" }} />
          <span className="sx-kbd">Échap</span>
        </div>

        <div className="max-h-[52vh] overflow-y-auto py-2">
          {flat.length === 0 && (
            <div className="px-4 py-8 text-center text-[13px]" style={{ color: "var(--muted-2)" }} data-testid="command-empty">
              {q.trim() ? "Aucun résultat." : "Commencez à taper pour rechercher."}
            </div>
          )}
          {groups.map((grp) => grp.key === "create" ? (
            <div key="create" className="mb-1">
              {grp.noResults && <div className="px-4 pt-2 pb-1 text-[12px]" style={{ color: "var(--muted-2)" }} data-testid="command-no-patient">Aucun patient trouvé</div>}
              {grp.rows.map((row) => {
                const on = row.idx === active;
                return (
                  <button key={row.idx} data-testid="command-create-patient" onMouseEnter={() => setActive(row.idx)} onClick={() => select(row.idx)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left" style={{ background: "#fdf8e1", outline: on ? "2px solid var(--accent)" : "none", outlineOffset: -2 }}>
                    <UserPlus className="w-4 h-4 shrink-0" style={{ color: "var(--ink)" }} />
                    <div className="flex-1 min-w-0 text-[13.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{row.titre}</div>
                    <span className="sx-kbd">↵</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div key={grp.key} className="mb-1">
              <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--muted-2)", letterSpacing: ".06em" }}>{grp.label}</div>
              {grp.rows.map((row) => {
                const on = row.idx === active;
                const Icon = row.Icon || grp.Icon;
                return (
                  <button key={row.idx} data-testid={`command-row-${row.idx}`} onMouseEnter={() => setActive(row.idx)} onClick={() => select(row.idx)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left" style={{ background: on ? "var(--row-selected)" : "transparent" }}>
                    <Icon className="w-4 h-4 shrink-0" style={{ color: "var(--muted)" }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{row.titre}</div>
                      {row.contexte && <div className="mono text-[11.5px] truncate" style={{ color: "var(--id)" }}>{row.contexte}</div>}
                      {row.extrait && <div className="text-[11.5px] truncate" style={{ color: "var(--muted)" }}>{row.extrait}</div>}
                    </div>
                    {on && <span className="sx-kbd">↵</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
